import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useState, useEffect, createContext, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Toaster } from "sonner";
import InstallPrompt from "./components/InstallPrompt";
import CookieConsent from "./components/CookieConsent";
import InstallAppButton from "./components/InstallAppButton";
import "@/App.css";

// Pages
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const MapView = lazy(() => import("./pages/MapView"));
const ResultsInput = lazy(() => import("./pages/ResultsInput"));
const ResultsView = lazy(() => import("./pages/ResultsView"));
const ResultsCollation = lazy(() => import("./pages/ResultsCollation"));
const Statistics = lazy(() => import("./pages/Statistics"));
const Candidates = lazy(() => import("./pages/Candidates"));
const PollingStations = lazy(() => import("./pages/PollingStations"));
const Subscription = lazy(() => import("./pages/Subscription"));
const LiveReporting = lazy(() => import("./pages/LiveReporting"));
const ComparativeAnalysis = lazy(() => import("./pages/ComparativeAnalysis"));
const AdminSubscriptions = lazy(() => import("./pages/AdminSubscriptions"));
const ProjectedWinners = lazy(() => import("./pages/ProjectedWinners"));
const ElectionNightDashboard = lazy(() => import("./pages/ElectionNightDashboard"));
const CouncilManagement = lazy(() => import("./pages/CouncilManagement"));
const CouncilResultsInput = lazy(() => import("./pages/CouncilResultsInput"));
const CouncilDetail = lazy(() => import("./pages/CouncilDetail"));
const PartyTrends = lazy(() => import("./pages/PartyTrends"));
const SenatorialManagement = lazy(() => import("./pages/SenatorialManagement"));
const HistoricalComparison = lazy(() => import("./pages/HistoricalComparison"));
const UnifiedPublicDashboard = lazy(() => import("./pages/UnifiedPublicDashboard"));
const VerificationPortal = lazy(() => import("./pages/VerificationPortal"));
const PresidentialManagement = lazy(() => import("./pages/PresidentialManagement"));
const ParliamentaryManagement = lazy(() => import("./pages/ParliamentaryManagement"));
const RegionalManagement = lazy(() => import("./pages/RegionalManagement"));
const AdvancedAnalytics = lazy(() => import("./pages/AdvancedAnalytics"));
const ComprehensiveAnalytics = lazy(() => import("./pages/ComprehensiveAnalytics"));
const Settings = lazy(() => import("./pages/Settings"));
const NotificationInbox = lazy(() => import("./pages/NotificationInbox"));
const Elections = lazy(() => import("./pages/Elections"));
const InteractiveMap = lazy(() => import("./pages/InteractiveMap"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const SubscriptionPlanManagement = lazy(() => import("./pages/SubscriptionPlanManagement"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CollaborationManagement = lazy(() => import("./pages/CollaborationManagement"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const APP_NAME = "EReMS Cameroon";

// Auth Context
export const AuthContext = createContext(null);

// Language Context
export const LanguageContext = createContext(null);

// Register service worker for offline support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Old per-type public URLs now open the unified dashboard
const LegacyPublicRedirect = () => {
  const { electionType } = useParams();
  return <Navigate to={`/public/dashboard?type=${electionType}`} replace />;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(localStorage.getItem('language') || 'en');

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    setCurrentLang(lang);
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <LanguageContext.Provider value={{ currentLang, changeLanguage }}>
        <div className="App min-h-screen bg-background">
          <Toaster 
            position="top-right" 
            richColors 
            theme="dark"
            toastOptions={{
              style: {
                background: 'hsl(220 15% 10%)',
                border: '1px solid hsl(220 15% 18%)',
              }
            }}
          />
          <InstallAppButton />
          <BrowserRouter>
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading...</div>}>
            <Routes>
              <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
              <Route path="/register" element={user ? <Navigate to="/" /> : <Register />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/map" element={
                <ProtectedRoute>
                  <MapView />
                </ProtectedRoute>
              } />
              <Route path="/results/input" element={
                <ProtectedRoute>
                  <ResultsInput />
                </ProtectedRoute>
              } />
              <Route path="/results" element={
                <ProtectedRoute>
                  <ResultsView />
                </ProtectedRoute>
              } />
              <Route path="/results/collation" element={
                <ProtectedRoute>
                  <ResultsCollation />
                </ProtectedRoute>
              } />
              <Route path="/statistics" element={
                <ProtectedRoute>
                  <Statistics />
                </ProtectedRoute>
              } />
              <Route path="/candidates" element={
                <ProtectedRoute>
                  <Candidates />
                </ProtectedRoute>
              } />
              <Route path="/polling-stations" element={
                <ProtectedRoute>
                  <PollingStations />
                </ProtectedRoute>
              } />
              <Route path="/subscription" element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              } />
              <Route path="/live" element={
                <ProtectedRoute>
                  <LiveReporting />
                </ProtectedRoute>
              } />
              <Route path="/advanced-analytics" element={
                <ProtectedRoute>
                  <AdvancedAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <NotificationInbox />
                </ProtectedRoute>
              } />
              <Route path="/admin/subscriptions" element={
                <ProtectedRoute>
                  <AdminSubscriptions />
                </ProtectedRoute>
              } />
              <Route path="/projected-winners" element={
                <ProtectedRoute>
                  <ProjectedWinners />
                </ProtectedRoute>
              } />
              <Route path="/election-night" element={
                <ElectionNightDashboard />
              } />
              <Route path="/council-management" element={
                <ProtectedRoute>
                  <CouncilManagement />
                </ProtectedRoute>
              } />
              <Route path="/council-results/input" element={
                <ProtectedRoute>
                  <CouncilResultsInput />
                </ProtectedRoute>
              } />
              <Route path="/council/:councilId" element={
                <ProtectedRoute>
                  <CouncilDetail />
                </ProtectedRoute>
              } />
              <Route path="/party-trends" element={
                <ProtectedRoute>
                  <PartyTrends />
                </ProtectedRoute>
              } />
              <Route path="/senatorial" element={
                <ProtectedRoute>
                  <SenatorialManagement />
                </ProtectedRoute>
              } />
              <Route path="/historical-comparison" element={
                <ProtectedRoute>
                  <HistoricalComparison />
                </ProtectedRoute>
              } />
              <Route path="/verification" element={
                <ProtectedRoute>
                  <VerificationPortal />
                </ProtectedRoute>
              } />
              <Route path="/presidential" element={
                <ProtectedRoute>
                  <PresidentialManagement />
                </ProtectedRoute>
              } />
              <Route path="/parliamentary" element={
                <ProtectedRoute>
                  <ParliamentaryManagement />
                </ProtectedRoute>
              } />
              <Route path="/regional" element={
                <ProtectedRoute>
                  <RegionalManagement />
                </ProtectedRoute>
              } />
              <Route path="/comprehensive-analytics" element={
                <ProtectedRoute>
                  <ComprehensiveAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <ComprehensiveAnalytics />
                </ProtectedRoute>
              } />
              <Route path="/elections" element={
                <ProtectedRoute>
                  <Elections />
                </ProtectedRoute>
              } />
              <Route path="/interactive-map" element={
                <ProtectedRoute>
                  <InteractiveMap />
                </ProtectedRoute>
              } />
              <Route path="/user-management" element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="/subscription-plan-management" element={
                <ProtectedRoute>
                  <SubscriptionPlanManagement />
                </ProtectedRoute>
              } />
              <Route path="/subscription/success" element={
                <ProtectedRoute>
                  <SubscriptionSuccess />
                </ProtectedRoute>
              } />
              <Route path="/collaboration-management" element={
                <ProtectedRoute>
                  <CollaborationManagement />
                </ProtectedRoute>
              } />
              {/* Public Routes - No Authentication Required */}
              <Route path="/public/results" element={<UnifiedPublicDashboard />} />
              <Route path="/public/dashboard" element={<UnifiedPublicDashboard />} />
              <Route path="/public/:electionType" element={<LegacyPublicRedirect />} />
              
              {/* Legal Policy Pages - No Authentication Required */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
            </Routes>
            </Suspense>
            <CookieConsent />
          </BrowserRouter>
          <InstallPrompt />
        </div>
      </LanguageContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
